import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  config,
  messages,
  currentLocale,
  digitalView,
  digitalChatMode,
  minimizeDigital,
  digitalModalPos,
  widgetClientId,
  fullPage,
} from '../../store/widget-store';
import { useI18n } from '../../hooks/use-i18n';
import { placementToStyle } from '../../hooks/use-bubble-drag';
import { useFrameDrag } from '../../hooks/use-frame-drag';
import { pickCsMessage } from '../../i18n/widget-content';
import { LanguageMenu } from '../chat/LanguageMenu';
import { IconBot, IconUser } from '../web/icons';
import { DigitalHumanStage } from './DigitalHumanStage';
import { WelcomePane } from './WelcomePane';
import { ChatPane } from './ChatPane';
import { toggleDigitalChatMode, resetDigitalChat } from './chat-actions';

/**
 * 沉浸式大弹窗外壳 —— 全屏遮罩 + 居中大弹窗(桌面左右分屏)。复刻设计稿 WelcomeScreen / AIChatScreen
 * 共用的 fixed inset-0 遮罩 + max-w-5xl 居中卡片。
 * - 常驻渲染 DigitalHumanStage(welcome / chat 共用,视频不卸载),右侧按 digitalView 渲染 WelcomePane / ChatPane。
 * - 移动端在右上角提供悬浮按钮组(语言 + 关闭);桌面端该组隐藏,按钮在各 pane 头部。
 * - Esc 关闭;遮罩空白点击不关闭(沉浸式防误触)。
 */
export function ImmersiveShell() {
  const cfg = config.value;
  const view = digitalView.value;
  const { t, dir } = useI18n();

  // 移动端(≤480,对应 widget.css 把弹窗改成全屏背景 + 底部 sheet 的断点)弹窗拖动禁用;
  // 用 resize 监听(与 WebChatPanel 的 isMobile 一致,且 jsdom 友好,不依赖 matchMedia)。
  const [isImmersiveMobile, setIsImmersiveMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsImmersiveMobile(window.innerWidth <= 480);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // 桌面居中弹窗可从 pane header 拖动(把手经 props 透传给 WelcomePane/ChatPane);
  // 移动端 / 整页 H5 下禁用。fixed:true —— .dcs-modal 默认 relative,需 fixed 脱离 overlay flex 居中。
  const modalDrag = useFrameDrag({
    enabled: !isImmersiveMobile && !fullPage.value,
    posSignal: digitalModalPos,
    slot: 'digital-modal',
    clientId: widgetClientId.value,
    fallbackW: 1024,
    fallbackH: 600,
    fixed: true,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // 整页 H5 模式不允许收起(收起只剩空白页 + 悬浮球已隐藏),退出交给小程序原生导航
      if (e.key === 'Escape' && !fullPage.value) minimizeDigital();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (!cfg) return null;

  const csName = pickCsMessage(cfg, 'name', currentLocale.value);

  // 弹窗定位:拖动中实时 dragStyle > 独立落点(fixed + placementToStyle + transform:none)> CSS flex 居中。
  // fixed 下 .dcs-modal 的 width:100% 相对视口,故叠加 maxWidth 上界(替代被 inline 覆盖掉的 CSS max-width:1024)。
  const FLOAT_MAX_W = 'min(1024px, calc(100vw - 16px))';
  const dragEnabled = !isImmersiveMobile && !fullPage.value;
  const persistedModalStyle: JSX.CSSProperties | undefined =
    dragEnabled && digitalModalPos.value
      ? {
          position: 'fixed',
          ...placementToStyle(digitalModalPos.value),
          transform: 'none',
          maxWidth: FLOAT_MAX_W,
        }
      : undefined;
  const modalStyle: JSX.CSSProperties | undefined = modalDrag.dragStyle
    ? { ...modalDrag.dragStyle, maxWidth: FLOAT_MAX_W }
    : persistedModalStyle;

  return (
    <div class="dcs-overlay" dir={dir}>
      <div
        class={`dcs-modal${modalDrag.dragging ? ' is-dragging' : ''}${
          modalStyle ? ' is-floating' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={csName}
        style={modalStyle}
      >
        <DigitalHumanStage />

        {/* 移动端右上悬浮按钮组(桌面 display:none);整页 H5 模式隐藏关闭键,仅留语言切换 */}
        <div class="dcs-float-actions">
          {/* 聊天视图:转人工/返回AI + 重置,移动端提到此处与语言切换并排
              (桌面端这组在 ChatPane header 内,float-actions 桌面隐藏,故不重复)。 */}
          {view === 'chat' && (
            <>
              <button
                type="button"
                class="dcs-icon-btn"
                aria-label={digitalChatMode.value === 'ai' ? t('web.switchToHuman') : t('web.switchToAI')}
                title={digitalChatMode.value === 'ai' ? t('web.switchToHuman') : t('web.switchToAI')}
                onClick={() => toggleDigitalChatMode()}
              >
                {digitalChatMode.value === 'ai' ? <IconUser size={20} /> : <IconBot size={20} />}
              </button>

              {/* 重置仅 AI 会话且已有消息时可用,与 ChatPane header 内一致 */}
              {digitalChatMode.value === 'ai' && messages.value.length > 0 && (
                <button
                  type="button"
                  class="dcs-icon-btn"
                  aria-label={t('web.resetChat')}
                  title={t('web.resetChat')}
                  onClick={() => void resetDigitalChat(t)}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                </button>
              )}
            </>
          )}

          <LanguageMenu />
          {!fullPage.value && (
            <button
              type="button"
              class="dcs-icon-btn"
              aria-label={t('panel.closeAria')}
              onClick={minimizeDigital}
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {view === 'welcome' ? (
          <WelcomePane dragHandleProps={modalDrag.handleProps} />
        ) : (
          <ChatPane dragHandleProps={modalDrag.handleProps} />
        )}
      </div>
    </div>
  );
}
